// The carrier questions loop's client-side rules (BRK-S290), pure. They mirror
// PMS's own (lib/carrier-questions.ts, PMS-S661) so the form shows and checks
// what the server will: a `dependsOn` question shows only while its parent's
// answer matches, a follow-up (`parentId`) sits under the question that opened
// it, and a blank answer or an empty list is no answer. PMS stays the
// authority — `complete` comes from the server; these only decide what to show
// and what to send. A carrier that answers from the application (Great
// American, PMS-S705) pre-fills what it partly knows, counted once a round
// sends it back, and settles the rest with any round, an empty one included.
import type {
  AnswerSource,
  CarrierAnswerValue,
  CarrierQuestion,
  QuoteAnswer,
} from "@/lib/submissions/brokerSubmissions";

/** The broker's working answers, by question id. */
export type AnswerValues = Record<string, CarrierAnswerValue>;

/** Whether a value counts as an answer (PMS's rule). */
export function hasAnswer(value: CarrierAnswerValue | undefined): boolean {
  return Array.isArray(value)
    ? value.length > 0
    : typeof value === "string" && value.trim() !== "";
}

/** The saved answers a set came back with. */
export function savedValues(questions: CarrierQuestion[]): AnswerValues {
  const values: AnswerValues = {};
  for (const q of questions) {
    if (q.answer !== undefined) values[q.id] = q.answer;
  }
  return values;
}

function shows(q: CarrierQuestion, values: AnswerValues): boolean {
  if (!q.dependsOn) return true;
  const parent = values[q.dependsOn.questionId];
  const given = Array.isArray(parent) ? parent : parent ? [parent] : [];
  return given.some((v) => q.dependsOn!.answers.includes(v));
}

/** A question in the order it renders, with how deep it sits under the
 *  question that opened it (0 = a top-level question). */
export interface PlacedQuestion {
  question: CarrierQuestion;
  depth: number;
}

/**
 * The questions that show, in render order: each follow-up directly under the
 * question that opened it (`parentId`), nested as deep as the carrier goes
 * (Great American: owners → an included owner's payroll). Siblings keep the
 * carrier's order. A follow-up whose parent isn't in the set is shown at the
 * top level rather than dropped; one whose opening question isn't in it
 * shows too, since the carrier asks it: Great American's read returns only
 * what is still unanswered (PMS-S815).
 */
export function placeQuestions(
  questions: CarrierQuestion[],
  values: AnswerValues,
): PlacedQuestion[] {
  // A follow-up shows only while the question that opened it shows too.
  const byId = new Map(questions.map((q) => [q.id, q]));
  const showing = new Map<string, boolean>();
  const visibleNow = (q: CarrierQuestion, trail: Set<string>): boolean => {
    const known = showing.get(q.id);
    if (known !== undefined) return known;
    const parent = q.parentId ? byId.get(q.parentId) : undefined;
    const opener = q.dependsOn && byId.get(q.dependsOn.questionId);
    const ok =
      (!opener || shows(q, values)) &&
      (!parent ||
        trail.has(parent.id) ||
        visibleNow(parent, new Set([...trail, q.id])));
    showing.set(q.id, ok);
    return ok;
  };
  const visible = questions.filter((q) => visibleNow(q, new Set()));
  const ids = new Set(visible.map((q) => q.id));
  const children = new Map<string, CarrierQuestion[]>();
  const roots: CarrierQuestion[] = [];
  for (const q of visible) {
    if (q.parentId && ids.has(q.parentId) && q.parentId !== q.id) {
      children.set(q.parentId, [...(children.get(q.parentId) ?? []), q]);
    } else {
      roots.push(q);
    }
  }
  const placed: PlacedQuestion[] = [];
  const seen = new Set<string>();
  const walk = (q: CarrierQuestion, depth: number) => {
    if (seen.has(q.id)) return; // a cycle in the carrier's data: show it once
    seen.add(q.id);
    placed.push({ question: q, depth });
    for (const child of children.get(q.id) ?? []) walk(child, depth + 1);
  };
  for (const q of roots) walk(q, 0);
  return placed;
}

/** What the step says of a saved answer that no longer fits its question
 *  (PMS-S744, BRK-S344); null when there is none. A choice is chosen again,
 *  anything typed answered again. */
export function previousAnswerNote(q: CarrierQuestion): string | null {
  const earlier = q.previousAnswer;
  if (earlier === undefined || !hasAnswer(earlier)) return null;
  const said = Array.isArray(earlier) ? earlier.join(", ") : earlier.trim();
  const again =
    q.type === "select" || q.type === "multi_select" || q.type === "boolean"
      ? "Choose again."
      : "Answer it again.";
  return `Your earlier answer, “${said}”, no longer exists. ${again}`;
}

/**
 * The heading the form shows above each placed question, or null: a top-level
 * question's `group`, where it differs from the previous top-level question's.
 * A follow-up is never headed, and doesn't end its group's run.
 */
export function questionHeadings(placed: PlacedQuestion[]): (string | null)[] {
  let previous: CarrierQuestion | undefined;
  return placed.map(({ question, depth }) => {
    if (depth > 0) return null;
    const heading =
      question.group && question.group !== previous?.group
        ? question.group
        : null;
    previous = question;
    return heading;
  });
}

/** The required questions that show and have no answer yet, in render order. */
export function unansweredRequired(
  questions: CarrierQuestion[],
  values: AnswerValues,
): CarrierQuestion[] {
  return placeQuestions(questions, values)
    .map((p) => p.question)
    .filter((q) => q.required && !hasAnswer(values[q.id]));
}

const sameAnswer = (
  a: CarrierAnswerValue | undefined,
  b: CarrierAnswerValue | undefined,
): boolean =>
  Array.isArray(a) && Array.isArray(b)
    ? a.length === b.length && a.every((v, i) => v === b[i])
    : a === b;

/**
 * The round to send: every showing question whose answer is new or changed
 * since it was saved, and every showing pre-fill, which counts only once it is
 * sent back (PMS-S705). Trimmed, with whose answer it is. An unchanged saved
 * answer isn't resent, so switching who is answering never relabels what was
 * saved.
 */
export function roundOfAnswers(
  questions: CarrierQuestion[],
  values: AnswerValues,
  source: AnswerSource,
): QuoteAnswer[] {
  return placeQuestions(questions, values)
    .map((p) => p.question)
    .filter((q) => hasAnswer(values[q.id]))
    .filter((q) => q.prefilled === true || !sameAnswer(values[q.id], q.answer))
    .map((q) => {
      const value = values[q.id];
      return {
        questionId: q.id,
        answer: Array.isArray(value) ? value : value.trim(),
        questionText: q.prompt,
        source,
      };
    });
}

/**
 * What Continue saves: the round, or an empty one when nothing is new but the
 * carrier isn't complete, because a round is also how what the application
 * settles reaches it (PMS-S705). Null when there is nothing to save.
 */
export function roundToSave(
  questions: CarrierQuestion[],
  values: AnswerValues,
  source: AnswerSource,
  complete: boolean,
): QuoteAnswer[] | null {
  const round = roundOfAnswers(questions, values, source);
  return round.length > 0 || !complete ? round : null;
}

/** Whether an answer is still the application's: a pre-fill the broker
 *  hasn't changed. */
export function fromApplication(
  q: CarrierQuestion,
  value: CarrierAnswerValue | undefined,
): boolean {
  return q.prefilled === true && sameAnswer(value, q.answer);
}

/** Who answered the saved set: the customer/insured only when every saved
 *  answer that names its source says so; the broker otherwise (the default). */
export function savedSource(questions: CarrierQuestion[]): AnswerSource {
  const sources = questions.flatMap((q) =>
    q.answerSource ? [q.answerSource] : [],
  );
  return sources.length > 0 && sources.every((s) => s === "insured")
    ? "insured"
    : "user";
}

/** A yes/no question's choices: the carrier's own option labels when it sends
 *  them (Great American, PIE), else PMS's "true"/"false" (Blitz). */
export function yesNoOptions(
  q: CarrierQuestion,
): { value: string; label: string }[] {
  return q.options && q.options.length > 0
    ? q.options.map((o) => ({ value: o, label: o }))
    : [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ];
}
